<template>
  <div class="is-pc pc-login">
    <div class="login-card">
      <h1>三高体检 · 内勤端</h1>
      <p class="sub">{{ taikangMode ? '使用手机号和机构编码登录指定机构' : '仅限机构内勤人员使用' }}</p>

      <div class="field">
        <label>手机号</label>
        <input
          v-model="loginForm.userPhone"
          type="tel"
          maxlength="11"
          placeholder="请输入手机号"
        />
      </div>

      <div v-if="taikangMode" class="field">
        <label>机构编码（orgId）</label>
        <input v-model.trim="loginForm.orgId" inputmode="numeric" maxlength="19" placeholder="请输入机构编码" @keyup.enter="handleLogin" />
      </div>

      <div v-else class="field">
        <label>验证码</label>
        <div class="code-row">
          <input
            v-model="loginForm.securityCode"
            maxlength="6"
            placeholder="请输入验证码"
            @keyup.enter="handleLogin"
          />
          <button class="code-btn" :disabled="!canGetCode" @click="handleGetCode">
            {{ codeButtonText }}
          </button>
        </div>
      </div>

      <button class="login-btn" :disabled="!canLogin || submitting" @click="handleLogin">
        {{ submitting ? "登录中..." : "登 录" }}
      </button>

      <!-- 暂时隐藏登录页使用引导入口，保留模板及逻辑，后续恢复时取消注释即可。
      <p v-if="!taikangMode" class="guide-link" @click="goGuide">
        <span>手机扫码关注公众号，也可在手机上使用内勤端</span>
        <em>查看使用引导 &gt;</em>
      </p>
      -->
    </div>

    <div v-if="submitting" class="loading-mask">
      <div class="loading-spinner"></div>
      <p>处理中...</p>
    </div>
    <org-picker v-if="!taikangMode && pendingLogin" :organizations="pendingLogin.organizations" :value="selectedOrgId" :busy="switchingOrg" title="选择登录机构" cancelable @select="selectedOrgId = $event" @confirm="confirmOrg" @cancel="pendingLogin = null" />
  </div>
</template>

<script>
import { pcToast } from "./pc-ui";
import { sendSanGaoStaffCode, loginSanGaoStaff, switchSanGaoStaffOrg } from "@/api/sanGaoStaff";
import OrgPicker from '../OrgPicker';
import { STAFF_TOKEN_KEY, STAFF_INFO_KEY, saveStaffSession } from '../staff-session';
import { isTaikangStaffMode } from '@/utils/sanGaoStaffMode';


const PHONE_REGEX = /^1[3-9]\d{9}$/;
const CODE_REGEX = /^\d{6}$/;

export default {
  name: "SanGaoStaffPcLogin",
  components: { OrgPicker },
  data() {
    return {
      loginForm: {
        userPhone: "",
        securityCode: "",
        orgId: typeof this.$route.query.orgId === 'string' ? this.$route.query.orgId : '',
      },
      countdown: 0,
      submitting: false,
      pendingLogin: null,
      selectedOrgId: null,
      switchingOrg: false,
    };
  },
  computed: {
    taikangMode() { return isTaikangStaffMode(this.$route.query); },
    validOrgCode() { const code = this.loginForm.orgId.trim(); return /^\d+$/.test(code) && Number.isSafeInteger(Number(code)) && Number(code) > 0; },
    canGetCode() {
      return !this.taikangMode && PHONE_REGEX.test(this.loginForm.userPhone) && this.countdown === 0;
    },
    codeButtonText() {
      return this.countdown > 0 ? `${this.countdown}s 后重发` : "获取验证码";
    },
    canLogin() {
      return (
        PHONE_REGEX.test(this.loginForm.userPhone) &&
        (this.taikangMode ? this.validOrgCode : CODE_REGEX.test(this.loginForm.securityCode))
      );
    },
  },
  watch: { taikangMode() { this.pendingLogin = null; this.selectedOrgId = null; this.loginForm.securityCode = ''; } },
  methods: {
    goGuide() {
      if (this.taikangMode) return;
      this.$router.push("/sanGao/staff/pc/guide");
    },
    startCountdown() {
      this.countdown = 60;
      const timer = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    },
    async handleGetCode() {
      if (!this.canGetCode) return;
      const res = await sendSanGaoStaffCode({ userPhone: this.loginForm.userPhone });
      if (res && res.code === 200) {
        pcToast("验证码已发送");
        this.startCountdown();
      } else {
        pcToast((res && res.msg) || "发送失败，请重试");
      }
    },
    async handleLogin() {
      if (!this.canLogin || this.submitting) return;
      const taikangMode = this.taikangMode;
      const orgId = Number(this.loginForm.orgId);
      this.submitting = true;
      try {
        const res = await loginSanGaoStaff(taikangMode ? {
          userPhone: this.loginForm.userPhone,
          orgId,
          taikangMode: true,
        } : {
          userPhone: this.loginForm.userPhone,
          securityCode: this.loginForm.securityCode,
        });
        if (this._isDestroyed || taikangMode !== this.taikangMode) return;
        if (res && res.code === 200 && res.data) {
          if (taikangMode && (res.data.taikangMode !== true || Number(res.data.orgId) !== orgId)) {
            pcToast('机构登录信息不匹配，请重试或联系管理员');
            return;
          }
          localStorage.removeItem(STAFF_TOKEN_KEY);
          localStorage.removeItem(STAFF_INFO_KEY);
          if (!taikangMode && (res.data.organizations || []).length > 1) {
            this.pendingLogin = res.data;
            this.selectedOrgId = null;
          } else {
            saveStaffSession(res.data);
            pcToast("登录成功");
            this.$router.replace({ path: '/sanGao/staff/pc/index', query: taikangMode ? { taikang: '1' } : this.$route.query });
          }
        } else {
          pcToast((res && res.msg) || "登录失败");
        }
      } catch (e) {
        pcToast("登录失败，请重试");
      } finally {
        this.submitting = false;
      }
    },
    async confirmOrg() {
      if (this.taikangMode || !this.pendingLogin || !this.selectedOrgId || this.switchingOrg) return;
      const pendingLogin = this.pendingLogin;
      this.switchingOrg = true;
      try {
        const res = await switchSanGaoStaffOrg(this.selectedOrgId, pendingLogin.token);
        if (this._isDestroyed || this.taikangMode || this.pendingLogin !== pendingLogin) return;
        if (!res || res.code !== 200 || !res.data) throw new Error((res && res.msg) || '切换机构失败');
        saveStaffSession(res.data);
        this.pendingLogin = null;
        pcToast('登录成功');
        this.$router.replace('/sanGao/staff/pc/index');
      } catch (e) {
        pcToast(e.message || '切换机构失败，请重试');
      } finally {
        this.switchingOrg = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.is-pc.pc-login {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(160deg, #065a9e 0%, #168de0 55%, #bfe6fb 100%);
}

.is-pc .login-card {
  width: 420px;
  padding: 48px 44px 40px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 20px 50px rgba(6, 51, 92, 0.25);
  text-align: center;
}

.is-pc .login-card h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #163b5c;
}

.is-pc .sub {
  margin: 8px 0 32px;
  font-size: 13px;
  color: #7c8d9c;
}

.is-pc .field {
  margin-bottom: 20px;
  text-align: left;
}

.is-pc .field label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  color: #4a6a82;
}

.is-pc .field input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid #d8e5ef;
  border-radius: 8px;
  font-size: 14px;
  color: #163b5c;
  outline: none;
  box-sizing: border-box;
}

.is-pc .field input:focus {
  border-color: #168de0;
}

.is-pc .code-row {
  display: flex;
  gap: 10px;
}

.is-pc .code-row input {
  flex: 1;
}

.is-pc .code-btn {
  flex-shrink: 0;
  width: 112px;
  height: 44px;
  border: 0;
  border-radius: 8px;
  font-size: 13px;
  color: #168de0;
  background: rgba(22, 141, 224, 0.1);
  cursor: pointer;
}

.is-pc .code-btn:disabled {
  color: #b8c6d2;
  background: #f1f5f8;
  cursor: not-allowed;
}

.is-pc .login-btn {
  width: 100%;
  height: 46px;
  margin-top: 10px;
  border: 0;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(100deg, #19a2e5, #146bd2);
  box-shadow: 0 6px 15px rgba(17, 112, 199, 0.22);
  cursor: pointer;
}

.is-pc .login-btn:disabled {
  color: rgba(255, 255, 255, 0.85);
  background: #b4cad9;
  box-shadow: none;
  cursor: not-allowed;
}

.is-pc .guide-link {
  margin: 16px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f4fafd;
  font-size: 12px;
  line-height: 1.6;
  color: #5b7488;
  text-align: center;
  cursor: pointer;
}

.is-pc .guide-link em {
  font-style: normal;
  display: block;
  color: #168de0;
}

.is-pc .guide-link:hover em {
  text-decoration: underline;
}

.is-pc .loading-mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: rgba(6, 51, 92, 0.35);
  color: #fff;
  font-size: 14px;
}

.is-pc .loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: pc-spin 0.8s linear infinite;
}

@keyframes pc-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
